const resistorColors = [
  { color: "black", digit: 0, multiplier: 1 },
  { color: "brown", digit: 1, multiplier: 10, tolerance: 1 },
  { color: "red", digit: 2, multiplier: 100, tolerance: 2 },
  { color: "orange", digit: 3, multiplier: 1000 },
  { color: "yellow", digit: 4, multiplier: 10000 },
  { color: "green", digit: 5, multiplier: 100000 },
  { color: "blue", digit: 6, multiplier: 1000000 },
  { color: "violet", digit: 7, multiplier: 10000000 },
  { color: "gray", digit: 8, multiplier: 100000000 },
  { color: "white", digit: 9, multiplier: 1000000000 }
];

const toleranceColors = [
  { color: "brown", tolerance: 1 },
  { color: "red", tolerance: 2 },
  { color: "gold", tolerance: 5 },
  { color: "silver", tolerance: 10 }
];

// Helper functions
const getColorByDigit = (digit) => resistorColors.find(c => c.digit === digit)?.color;
const getColorByMultiplier = (multiplier) => resistorColors.find(c => c.multiplier === multiplier)?.color;
const getDigitByColor = (color) => resistorColors.find(c => c.color === color)?.digit;
const getMultiplierByColor = (color) => resistorColors.find(c => c.color === color)?.multiplier;
const getToleranceByColor = (color) => toleranceColors.find(c => c.color === color)?.tolerance;

const formatResistance = (resistance) => {
  if (resistance >= 1e9) {
    return (resistance / 1e9).toFixed(1) + ' GΩ';
  } else if (resistance >= 1e6) {
    return (resistance / 1e6).toFixed(1) + ' MΩ';
  } else if (resistance >= 1e3) {
    return (resistance / 1e3).toFixed(1) + ' kΩ';
  } else {
    return resistance.toFixed(0) + ' Ω';
  }
};

const generateWrongAnswers = (correctAnswer, allAnswers, count = 3) => {
  const wrongAnswers = allAnswers
    .filter(answer => answer !== correctAnswer)
    .sort(() => Math.random() - 0.5)
    .slice(0, count);
  return wrongAnswers;
};

const generateResistorQuiz = () => {
  const questions = [];
  let questionId = 1;

  // Generate color-to-digit memorization questions
  resistorColors.slice(0, 10).forEach((color, index) => {
    if (index % 3 === 0) { // Every third color to avoid too many questions
      const wrongDigits = generateWrongAnswers(color.digit, resistorColors.map(c => c.digit), 3);
      const digitOptions = [color.digit, ...wrongDigits].sort(() => Math.random() - 0.5);
      const correctIndex = digitOptions.indexOf(color.digit);
      
      questions.push({
        id: questionId++,
        question: `What digit does the color ${color.color} represent?`,
        options: digitOptions.map(d => d.toString()),
        correct: correctIndex,
        explanation: `The color ${color.color} represents the digit ${color.digit}.`
      });
    }
  });

  // Generate digit-to-color questions
  [0, 1, 2, 3, 5, 7, 9].forEach(digit => {
    const correctColor = getColorByDigit(digit);
    const wrongColors = generateWrongAnswers(correctColor, resistorColors.map(c => c.color), 3);
    const colorOptions = [correctColor, ...wrongColors].sort(() => Math.random() - 0.5);
    const correctIndex = colorOptions.indexOf(correctColor);
    
    questions.push({
      id: questionId++,
      question: `What color represents the digit ${digit}?`,
      options: colorOptions,
      correct: correctIndex,
      explanation: `The digit ${digit} is represented by the color ${correctColor}.`
    });
  });

  // Generate resistance calculation questions (given colors, find resistance)
  const sampleResistors = [
    { band1: "brown", band2: "black", multiplier: "red", tolerance: "gold" }, // 1kΩ ±5%
    { band1: "red", band2: "red", multiplier: "brown", tolerance: "gold" }, // 220Ω ±5%
    { band1: "orange", band2: "orange", multiplier: "orange", tolerance: "gold" }, // 33kΩ ±5%
    { band1: "yellow", band2: "violet", multiplier: "red", tolerance: "gold" }, // 4.7kΩ ±5%
    { band1: "green", band2: "blue", multiplier: "brown", tolerance: "gold" }, // 560Ω ±5%
    { band1: "brown", band2: "black", multiplier: "black", tolerance: "brown" }, // 10Ω ±1%
    { band1: "red", band2: "red", multiplier: "red", tolerance: "red" }, // 2.2kΩ ±2%
    { band1: "brown", band2: "black", multiplier: "orange", tolerance: "gold" } // 10kΩ ±5%
  ];

  sampleResistors.forEach(resistor => {
    const digit1 = getDigitByColor(resistor.band1);
    const digit2 = getDigitByColor(resistor.band2);
    const multiplier = getMultiplierByColor(resistor.multiplier);
    const tolerance = getToleranceByColor(resistor.tolerance);
    const resistance = (digit1 * 10 + digit2) * multiplier;
    const formattedResistance = formatResistance(resistance);
    
    // Generate wrong resistance values
    const wrongResistances = [
      formatResistance(resistance * 2),
      formatResistance(resistance * 0.1),
      formatResistance(resistance * 10)
    ];
    const resistanceOptions = [formattedResistance, ...wrongResistances].sort(() => Math.random() - 0.5);
    const correctIndex = resistanceOptions.indexOf(formattedResistance);
    
    questions.push({
      id: questionId++,
      question: `A resistor has color bands: ${resistor.band1}, ${resistor.band2}, ${resistor.multiplier}, ${resistor.tolerance}. What is its resistance value?`,
      options: resistanceOptions,
      correct: correctIndex,
      explanation: `Color bands ${resistor.band1} (${digit1}), ${resistor.band2} (${digit2}), ${resistor.multiplier} (×${multiplier}), ${resistor.tolerance} (±${tolerance}%) give: (${digit1}${digit2}) × ${multiplier} = ${formattedResistance} ±${tolerance}%.`
    });
  });

  // Generate color sequence questions (given resistance, find colors)
  const reverseResistors = [
    { resistance: 1000, unit: "1 kΩ" },
    { resistance: 470, unit: "470 Ω" },
    { resistance: 2200, unit: "2.2 kΩ" },
    { resistance: 100, unit: "100 Ω" }
  ];

  reverseResistors.forEach(({ resistance, unit }) => {
    // Calculate the color bands for this resistance
    const resistanceStr = resistance.toString();
    const significantDigits = resistanceStr.substring(0, 2);
    const multiplierPower = resistanceStr.length - 2;
    const multiplierValue = Math.pow(10, multiplierPower);
    
    const band1Color = getColorByDigit(parseInt(significantDigits[0]));
    const band2Color = getColorByDigit(parseInt(significantDigits[1]));
    const multiplierColor = getColorByMultiplier(multiplierValue);
    
    if (band1Color && band2Color && multiplierColor) {
      const correctSequence = `${band1Color}, ${band2Color}, ${multiplierColor}`;
      const wrongSequences = [
        `${band2Color}, ${band1Color}, ${multiplierColor}`,
        `${band1Color}, ${band2Color}, ${getColorByMultiplier(multiplierValue * 10) || 'black'}`,
        `${getColorByDigit((parseInt(significantDigits[0]) + 1) % 10)}, ${band2Color}, ${multiplierColor}`
      ];
      const sequenceOptions = [correctSequence, ...wrongSequences].sort(() => Math.random() - 0.5);
      const correctIndex = sequenceOptions.indexOf(correctSequence);
      
      questions.push({
        id: questionId++,
        question: `What are the first three color bands for a ${unit} resistor?`,
        options: sequenceOptions,
        correct: correctIndex,
        explanation: `For ${unit}: first two digits are ${significantDigits[0]} (${band1Color}) and ${significantDigits[1]} (${band2Color}), multiplier is ×${multiplierValue} (${multiplierColor}).`
      });
    }
  });

  // Add tolerance questions
  const toleranceQuestions = [
    {
      id: questionId++,
      question: "What tolerance does a gold band represent?",
      options: ["5%", "10%", "1%", "2%"],
      correct: 0,
      explanation: "A gold tolerance band represents ±5% tolerance."
    },
    {
      id: questionId++,
      question: "What tolerance does a silver band represent?",
      options: ["10%", "5%", "1%", "20%"],
      correct: 0,
      explanation: "A silver tolerance band represents ±10% tolerance."
    },
    {
      id: questionId++,
      question: "Which tolerance band indicates the most precise resistor?",
      options: ["Brown (±1%)", "Gold (±5%)", "Silver (±10%)", "Red (±2%)"],
      correct: 0,
      explanation: "Brown tolerance band (±1%) indicates the most precise resistor with the smallest tolerance."
    },
    {
      id: questionId++,
      question: "In a 4-band resistor, which band represents the tolerance?",
      options: ["4th band (rightmost)", "1st band (leftmost)", "3rd band", "2nd band"],
      correct: 0,
      explanation: "In a 4-band resistor, the 4th band (rightmost) represents the tolerance."
    }
  ];

  questions.push(...toleranceQuestions);

  // Add general knowledge questions
  const generalQuestions = [
    {
      id: questionId++,
      question: "How many significant digits do the first two bands represent in a 4-band resistor?",
      options: ["Two digits", "One digit", "Three digits", "Four digits"],
      correct: 0,
      explanation: "In a 4-band resistor, the first two bands represent two significant digits of the resistance value."
    },
    {
      id: questionId++,
      question: "What does the third band represent in a 4-band resistor?",
      options: ["Multiplier", "Second digit", "Tolerance", "First digit"],
      correct: 0,
      explanation: "The third band in a 4-band resistor represents the multiplier (the power of 10 to multiply the first two digits by)."
    },
    {
      id: questionId++,
      question: "Which direction should you read a resistor's color bands?",
      options: ["Left to right (tolerance band on the right)", "Right to left (tolerance band on the left)", "Either direction", "From the center outward"],
      correct: 0,
      explanation: "Resistor color bands are read from left to right, with the tolerance band (usually gold or silver) positioned on the right side."
    }
  ];

  questions.push(...generalQuestions);

  // Shuffle questions and limit to reasonable number
  return questions.sort(() => Math.random() - 0.5).slice(0, 25);
};

export const resistorsQuizData = {
  id: 'resistors-quiz',
  title: 'Resistor Color Code Quiz',
  subtitle: 'Master resistor color code calculations',
  description: 'Test your knowledge of resistor color codes and resistance calculations',
  questions: generateResistorQuiz()
};

export default resistorsQuizData;